import { Bot, Check, CornerDownLeft, Info, Link as LinkIcon, User } from 'lucide-react';
import React from 'react';
import ReactMarkdown from 'react-markdown';
import type { Components } from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';
import { ChatMessage } from '../types';
import { openAuthenticatedPdf } from '../services/api';
import { Citation } from './Citation';
import KpiQuadrant from './KpiQuadrant';

const markdownSanitizeSchema = {
    ...defaultSchema,
    tagNames: [...(defaultSchema.tagNames || []), 'span'],
    attributes: {
        ...defaultSchema.attributes,
        span: [
            ...((defaultSchema.attributes?.span as Array<string | [string, ...Array<string | number>]>) || []),
            ['className', 'command-hint', 'analysis-params-tooltip'],
            'data-command',
            'title',
        ],
    },
};

const markdownRehypePlugins = [rehypeRaw, [rehypeSanitize, markdownSanitizeSchema]];

const AILoadingView: React.FC = () => {
    const [step, setStep] = React.useState(0);
    const steps = [
        'Synthesizing reliability reports...',
        'Identifying performance patterns...',
        'Analyzing fleet-wide metrics...',
        'Computing actionable insights...',
        'Finalizing comprehensive summary...'
    ];

    React.useEffect(() => {
        const interval = setInterval(() => {
            setStep((s) => (s + 1) % steps.length);
        }, 3000);
        return () => clearInterval(interval);
    }, [steps.length]);

    return (
        <div className="analysis-summary loading">
            <div className="summary-loading-indicator">
                <div className="thinking-text-shimmer">{steps[step]}</div>
                <div className="summary-skeleton">
                    <div className="skeleton-line"></div>
                    <div className="skeleton-line medium"></div>
                    <div className="skeleton-line short"></div>
                </div>
            </div>
        </div>
    );
};

const preprocessMarkdown = (text: string) => {
    if (!text) return '';
    // Normalize any [[any text]](url) to [PDF_SOURCE](url) — LLM sometimes uses
    // section/KPI names instead of "Source Report" as the citation label.
    let processed = text.replace(/\[\[[^\]]+\]\]\(([^)]+)\)/g, '[PDF_SOURCE]($1)');
    processed = processed.replace(/\[\[Source Report\]\]/g, '[PDF_SOURCE]');
    processed = processed.replace(/\[\[(\/pdf\?.*?)\]\]/g, '[PDF_SOURCE]($1)');
    return processed;
};

const extractText = (value: React.ReactNode): string => {
    if (typeof value === 'string' || typeof value === 'number') return String(value);
    if (Array.isArray(value)) return value.map(extractText).join('');
    if (React.isValidElement(value)) return extractText(value.props.children);
    return '';
};

const extractCommand = (props: { ['data-command']?: unknown; dataCommand?: unknown }, children: React.ReactNode): string => {
    const raw = props['data-command'] ?? props.dataCommand;
    if (typeof raw === 'string' && raw.trim()) {
        return raw;
    }

    const childText = extractText(children).trim();
    return childText || String(children);
};

const stripHtml = (value?: string): string => {
    if (!value) return '';
    const parsed = new DOMParser().parseFromString(value, 'text/html');
    return parsed.body.textContent?.trim() || '';
};

const stripPartConfirmationPrefix = (text?: string): string | undefined => {
    if (!text) return undefined;
    return text.replace(/^Found\s+\*{0,2}\d+\s+part\s+match(?:es)?\*{0,2}\s+for\s+/i, '');
};

const isPdfHref = (href?: string): boolean => {
    if (!href) return false;
    return href.startsWith('/pdf') || href.includes('/pdf?') || href.includes('/pdf#') || href.includes('/api/pdf');
};

const isCitationLabel = (children: React.ReactNode): boolean => {
    const text = extractText(children).trim();
    return text === 'PDF_SOURCE' || text === 'Source Report';
};

const resolveLinkUrl = (href?: string): string => {
    if (!href) return '';
    if (!href.startsWith('/pdf') && !href.startsWith('/api/pdf')) return href;

    const apiUrl = import.meta.env.VITE_API_URL || '';
    try {
        const baseUrl = new URL(apiUrl);
        const linkUrl = new URL(href, baseUrl.origin);
        return linkUrl.toString();
    } catch {
        const baseUrl = apiUrl.replace(/\/chat\/?$/, '');
        return `${baseUrl}${href}`;
    }
};

interface MarkdownComponentOptions {
    onPrefill?: (value: string) => void;
    issues?: ChatMessage['issues'];
    enableKpiTooltip?: boolean;
    enableAnalysisParamsTooltip?: boolean;
    unwrapParagraph?: boolean;
}

const createMarkdownComponents = (options: MarkdownComponentOptions): Components => {
    const components: Components = {
        a: ({ href, children, ...props }) => {
        const anchorProps = { ...props } as { node?: unknown };
        delete anchorProps.node;

        const linkHref = typeof href === 'string' ? href : undefined;
        const isPdfLink = isPdfHref(linkHref);
        const isCitation = isCitationLabel(children) || isPdfLink;
        const fullUrl = resolveLinkUrl(linkHref);

        const linkText = extractText(children).trim();
        const matchingIssue = options.enableKpiTooltip
            ? options.issues?.find(issue => issue.kpi === linkText)
            : undefined;

        return (
            <>
                <a
                    href={fullUrl}
                    target={isPdfLink ? '_blank' : undefined}
                    rel={isPdfLink ? 'noopener noreferrer' : undefined}
                    onClick={(event) => {
                        if (isPdfLink) {
                            event.preventDefault();
                            event.stopPropagation();
                            window.open(fullUrl, '_blank', 'noopener,noreferrer');
                        }
                    }}
                    className={isCitation ? 'source-icon-link' : ''}
                    title={isCitation ? 'View Source Report' : undefined}
                    {...anchorProps}
                >
                    {isCitation ? <LinkIcon size={12} /> : children}
                </a>
                {matchingIssue?.excerpt && (
                    <span className="kpi-tooltip-wrapper">
                        <span className="tooltip-trigger"><Info size={14} /></span>
                        <span className="tooltip-content">{matchingIssue.excerpt}</span>
                    </span>
                )}
            </>
        );
    },
        span: ({ className, title, children, ...props }) => {
        const spanProps = { ...props } as { node?: unknown; ['data-command']?: unknown; dataCommand?: unknown };
        delete spanProps.node;

        const spanClass = typeof className === 'string' ? className : undefined;

        if (spanClass === 'command-hint') {
            return <CommandHint command={extractCommand(spanProps, children)} onPrefill={options.onPrefill} />;
        }

        if (spanClass === 'analysis-params-tooltip' && typeof title === 'string' && options.enableAnalysisParamsTooltip) {
            return (
                <span className="analysis-params-tooltip-wrapper" {...spanProps}>
                    <span className="tooltip-trigger"><Info size={16} /></span>
                    <span className="tooltip-content">{title}</span>
                </span>
            );
        }

        return <span className={spanClass} title={typeof title === 'string' ? title : undefined} {...spanProps}>{children}</span>;
    },
    };

    if (options.unwrapParagraph) {
        components.p = ({ children }) => <>{children}</>;
    }

    return components;
};

const RecommendationView: React.FC<{ content: string; partName?: string; onPrefill?: (value: string) => void }> = ({ content, partName, onPrefill }) => {
    const containerRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        const timer = setTimeout(() => {
            containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }, 150);
        return () => clearTimeout(timer);
    }, []);

    return (
        <div ref={containerRef} className="actionable-recommendations">
            <h4>
                <span>Actionable Recommendations for {stripHtml(partName) || 'selected part'} (experimental)</span>
            </h4>
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw]}
                components={{
                    a: ({ href, children, ...props }) => {
                        const isPdfLink = href && (href.startsWith('/pdf') || href.includes('/pdf?') || href.includes('/api/pdf'));
                        const isLabeledCitation = Array.isArray(children)
                            ? (children[0] === 'PDF_SOURCE' || children[0] === 'Source Report')
                            : (children === 'PDF_SOURCE' || children === 'Source Report');
                        const isCitation = isLabeledCitation || isPdfLink;
                        let fullUrl = href || '';

                        if (isPdfLink && href) {
                            const apiUrl = import.meta.env.VITE_API_URL || '';
                            try {
                                const baseUrl = new URL(apiUrl);
                                const linkUrl = new URL(href, baseUrl.origin);
                                fullUrl = linkUrl.toString();
                            } catch (err) {
                                const baseUrl = apiUrl.replace(/\/chat\/?$/, '');
                                fullUrl = `${baseUrl}${href}`;
                            }
                        }

                        return (
                            <a
                                href={fullUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={isPdfLink ? (e) => { e.preventDefault(); e.stopPropagation(); openAuthenticatedPdf(fullUrl); } : undefined}
                                className={isCitation ? 'source-icon-link' : ''}
                                title={isCitation ? 'View Source Report' : undefined}
                                {...props}
                            >
                                {isCitation ? <LinkIcon size={12} /> : children}
                            </a>
                        );
                    },
                    span: ({ className, title, children, ...props }) => {
                        if (className === 'command-hint') {
                            const cmd = (props as Record<string, string>)['data-command'] || (typeof children === 'string' ? children : String(children));
                            return <CommandHint command={cmd} onPrefill={onPrefill} />;
                        }
                        return <span className={className} title={title} {...props}>{children}</span>;
                    }
                }}
            >
                {preprocessMarkdown(content)}
            </ReactMarkdown>
        </div>
    );
};

const CommandHint: React.FC<{ command: string; onPrefill?: (value: string) => void }> = ({ command, onPrefill }) => {
    const [copied, setCopied] = React.useState(false);

    const handleClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        navigator.clipboard.writeText(command);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        onPrefill?.(command);
    };

    return (
        <span className="command-hint-wrapper">
            <strong>{command}</strong>
            <button className="command-hint-btn" onClick={handleClick} title={`Stage "${command}" in input`}>
                {copied ? <Check size={12} className="check-icon" /> : <CornerDownLeft size={12} />}
            </button>
        </span>
    );
};

interface MessageListProps {
    messages: ChatMessage[];
    onPrefillInput?: (value: string) => void;
}

export const MessageList: React.FC<MessageListProps> = ({ messages, onPrefillInput }) => {
    if (messages.length === 0) {
        return (
            <div className="empty-state">
                <Bot size={48} className="empty-icon" />
                <h3>Issue Analysis Chatbot</h3>
                <p>Enter Aircraft (MDS), Part Number (WUC/NIIN), or Part Description to analyze Executive reports for flagged issues.</p>
            </div>
        );
    }

    return (
        <div className="message-list">
            {messages.map((message, index) => {
                const isUser = message.role === 'user';
                const isConfirmation = message.isConfirmation;

                const prevMessage = index > 0 ? messages[index - 1] : null;
                const showHeader = !prevMessage ||
                    prevMessage.role !== message.role ||
                    (prevMessage.role === 'assistant' && !prevMessage.isConfirmation);
                const hasQuadrant = !!(message.showQuadrant && (
                    (message.issues && message.issues.length > 0) ||
                    message.maintenanceRunId ||
                    message.logisticsRunId
                ));
                const cleanPartDescription = stripPartConfirmationPrefix(message.partConfirmation) || (message.partName ? stripHtml(message.partName) : undefined);


                return (
                    <div key={message.id} data-message-index={index} className={`message ${message.role} ${message.inQAMode ? 'qa-mode' : ''} ${isConfirmation ? 'confirmation' : ''}`}>
                        {showHeader && (
                            <div className="message-header">
                                <div className="message-avatar">
                                    {isUser ? <User size={16} /> : <Bot size={16} />}
                                </div>
                                <div className="message-meta">
                                    <span className="message-time">
                                        {message.timestamp.toLocaleTimeString()}
                                    </span>
                                </div>
                            </div>
                        )}
                        <div className="message-content">
                            <div className="message-text">
                                {hasQuadrant && (
                                    <KpiQuadrant
                                        issues={message.issues || []}
                                        hasRdtReport={!!message.maintenanceRunId}
                                        hasRldtReport={!!message.logisticsRunId}
                                        partDescription={cleanPartDescription}
                                        onPrefillInput={onPrefillInput}
                                    />
                                )}
                                {!hasQuadrant && message.inQAMode && cleanPartDescription && (
                                    <div className="kpi-tree-part-description">
                                        <ReactMarkdown>{cleanPartDescription}</ReactMarkdown>
                                    </div>
                                )}
                                <ReactMarkdown
                                    remarkPlugins={[remarkGfm]}
                                    rehypePlugins={[rehypeRaw]}
                                    components={{
                                        a: ({ href, children, ...props }) => {
                                            let fullUrl = href || '';
                                            const isPdfLink = href && (
                                                href.startsWith('/pdf') ||
                                                href.includes('/pdf?') ||
                                                href.includes('/pdf#') ||
                                                href.includes('/api/pdf')
                                            );
                                            const isCitation = Array.isArray(children)
                                                ? (children[0] === 'PDF_SOURCE' || children[0] === 'Source Report')
                                                : (children === 'PDF_SOURCE' || children === 'Source Report');

                                            if (href && (href.startsWith('/pdf') || href.startsWith('/api/pdf'))) {
                                                const apiUrl = import.meta.env.VITE_API_URL || '';
                                                try {
                                                    const baseUrl = new URL(apiUrl);
                                                    const linkUrl = new URL(href, baseUrl.origin);
                                                    fullUrl = linkUrl.toString();
                                                } catch (err) {
                                                    const baseUrl = apiUrl.replace(/\/chat\/?$/, '');
                                                    fullUrl = `${baseUrl}${href}`;
                                                }
                                            }

                                            const linkText = typeof children === 'string' ? children :
                                                Array.isArray(children) ? children.join('') : '';
                                            const matchingIssue = message.issues?.find(issue =>
                                                issue.kpi === linkText
                                            );

                                            return (
                                                <>
                                                    <a
                                                        href={fullUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        onClick={isPdfLink ? (e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            openAuthenticatedPdf(fullUrl);
                                                        } : undefined}
                                                        className={isCitation ? 'source-icon-link' : ''}
                                                        title={isCitation ? 'View Source Report' : undefined}
                                                        {...props}
                                                    >
                                                        {isCitation ? <LinkIcon size={12} /> : children}
                                                    </a>
                                                    {matchingIssue?.excerpt && (
                                                        <span className="kpi-tooltip-wrapper">
                                                            <span className="tooltip-trigger"><Info size={14} /></span>
                                                            <span className="tooltip-content">{matchingIssue.excerpt}</span>
                                                        </span>
                                                    )}
                                                </>
                                            );
                                        },
                                        span: ({ className, title, children, ...props }) => {
                                            if (className === 'command-hint') {
                                                const cmd = (props as Record<string, string>)['data-command'] || (typeof children === 'string' ? children : String(children));
                                                return <CommandHint command={cmd} onPrefill={onPrefillInput} />;
                                            }
                                            if (className === 'analysis-params-tooltip' && title) {
                                                return (
                                                    <span className="analysis-params-tooltip-wrapper" {...props}>
                                                        <span className="tooltip-trigger"><Info size={16} /></span>
                                                        <span className="tooltip-content">{title}</span>
                                                    </span>
                                                );
                                            }
                                            return <span className={className} title={title} {...props}>{children}</span>;
                                        },
                                    }}
                                >
                                    {preprocessMarkdown(message.content)}
                                </ReactMarkdown>
                                {message.formattedIssues && !message.analysisSummary && !message.summaryLoading && (
                                    <div className="structured-issues">
                                        <ReactMarkdown
                                            remarkPlugins={[remarkGfm]}
                                            rehypePlugins={[rehypeRaw]}
                                            components={{
                                                a: ({ href, children, ...props }) => {
                                                    const isPdfLink = href && (href.startsWith('/pdf') || href.includes('/pdf?') || href.includes('/api/pdf'));
                                                    const isLabeledCitation = Array.isArray(children)
                                                        ? (children[0] === 'PDF_SOURCE' || children[0] === 'Source Report')
                                                        : (children === 'PDF_SOURCE' || children === 'Source Report');
                                                    const isCitation = isLabeledCitation || isPdfLink;
                                                    let fullUrl = href || '';

                                                    if (isPdfLink && href) {
                                                        const apiUrl = import.meta.env.VITE_API_URL || '';
                                                        try {
                                                            const baseUrl = new URL(apiUrl);
                                                            const linkUrl = new URL(href, baseUrl.origin);
                                                            fullUrl = linkUrl.toString();
                                                        } catch (err) {
                                                            const baseUrl = apiUrl.replace(/\/chat\/?$/, '');
                                                            fullUrl = `${baseUrl}${href}`;
                                                        }
                                                    }

                                                    return (
                                                        <a
                                                            href={fullUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            onClick={isPdfLink ? (e) => { e.preventDefault(); e.stopPropagation(); openAuthenticatedPdf(fullUrl); } : undefined}
                                                            className={isCitation ? 'source-icon-link' : ''}
                                                            title={isCitation ? 'View Source Report' : undefined}
                                                            {...props}
                                                        >
                                                            {isCitation ? <LinkIcon size={12} /> : children}
                                                        </a>
                                                    );
                                                },
                                                span: ({ className, title, children, ...props }) => {
                                                    if (className === 'command-hint') {
                                                        const cmd = (props as Record<string, string>)['data-command'] || (typeof children === 'string' ? children : String(children));
                                                        return <CommandHint command={cmd} onPrefill={onPrefillInput} />;
                                                    }
                                                    return <span className={className} title={title} {...props}>{children}</span>;
                                                }
                                            }}
                                        >
                                            {preprocessMarkdown(message.formattedIssues)}
                                        </ReactMarkdown>
                                    </div>
                                )}
                                {(message.summaryLoading || message.analysisSummary) && (
                                    <div className="analysis-summary">
                                        {cleanPartDescription && (
                                            <div className="kpi-tree-part-description">
                                                <ReactMarkdown>{cleanPartDescription}</ReactMarkdown>
                                            </div>
                                        )}
                                        {message.summaryLoading && <AILoadingView />}
                                        {!message.summaryLoading && message.analysisSummary && (
                                        <ReactMarkdown
                                            remarkPlugins={[remarkGfm]}
                                            rehypePlugins={[rehypeRaw]}
                                            components={{
                                                a: ({ href, children, ...props }) => {
                                                    const isPdfLink = href && (href.startsWith('/pdf') || href.includes('/pdf?') || href.includes('/api/pdf'));
                                                    const isLabeledCitation = Array.isArray(children)
                                                        ? (children[0] === 'PDF_SOURCE' || children[0] === 'Source Report')
                                                        : (children === 'PDF_SOURCE' || children === 'Source Report');
                                                    const isCitation = isLabeledCitation || isPdfLink;
                                                    let fullUrl = href || '';

                                                    if (isPdfLink && href) {
                                                        const apiUrl = import.meta.env.VITE_API_URL || '';
                                                        try {
                                                            const baseUrl = new URL(apiUrl);
                                                            const linkUrl = new URL(href, baseUrl.origin);
                                                            fullUrl = linkUrl.toString();
                                                        } catch (err) {
                                                            const baseUrl = apiUrl.replace(/\/chat\/?$/, '');
                                                            fullUrl = `${baseUrl}${href}`;
                                                        }
                                                    }

                                                    return (
                                                        <a
                                                            href={fullUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            onClick={isPdfLink ? (e) => { e.preventDefault(); e.stopPropagation(); openAuthenticatedPdf(fullUrl); } : undefined}
                                                            className={isCitation ? 'source-icon-link' : ''}
                                                            title={isCitation ? 'View Source Report' : undefined}
                                                            {...props}
                                                        >
                                                            {isCitation ? <LinkIcon size={12} /> : children}
                                                        </a>
                                                    );
                                                },
                                                span: ({ className, title, children, ...props }) => {
                                                    if (className === 'command-hint') {
                                                        const cmd = (props as Record<string, string>)['data-command'] || (typeof children === 'string' ? children : String(children));
                                                        return <CommandHint command={cmd} onPrefill={onPrefillInput} />;
                                                    }
                                                    return <span className={className} title={title} {...props}>{children}</span>;
                                                }
                                            }}
                                        >
                                            {preprocessMarkdown(message.analysisSummary)}
                                        </ReactMarkdown>
                                        )}
                                    </div>
                                )}

                                {!message.summaryLoading && message.recommendations && (
                                    <div className="recommendations-container">
                                        <RecommendationView
                                            content={message.recommendations}
                                            partName={message.partName}
                                            onPrefill={onPrefillInput}
                                        />
                                    </div>
                                )}
                            </div>

                            {message.partsList && message.partsList.length > 0 && (
                                <div className="message-parts-list">
                                    <h4>{message.matchingPartsCount === 1 ? 'Part Details:' : 'Did you mean?'}</h4>
                                    <ul className="parts-list">
                                        {message.partsList.map((part, partIndex) => (
                                            <li key={partIndex} className="part-item">
                                                <ReactMarkdown
                                                    remarkPlugins={[remarkGfm]}
                                                    rehypePlugins={markdownRehypePlugins as []}
                                                    components={createMarkdownComponents({
                                                        onPrefill: onPrefillInput,
                                                        unwrapParagraph: true,
                                                    })}
                                                >
                                                    {part.part_name || part.part_identifier || ''}
                                                </ReactMarkdown>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {message.citations && message.citations.length > 0 && (
                                <div className="message-citations">
                                    <h4>Sources:</h4>
                                    {message.citations.map((citation, citationIndex) => (
                                        <Citation
                                            key={citationIndex}
                                            citation={citation}
                                            index={citationIndex + 1}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};
